import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { NavLink, WorkspaceSidebarProps, normalizeNavUrl } from "./types";
import styles from "./WorkspaceSidebar.module.scss";

const isPathActive = (pathname: string, url?: string) => {
  if (!url) return false;
  const normalized = normalizeNavUrl(url);
  return pathname === normalized || pathname.startsWith(`${normalized}/`);
};

const hasActiveChild = (pathname: string, item: NavLink): boolean => {
  if (item.url && isPathActive(pathname, item.url)) return true;
  return Boolean(item.sublinks?.some((child) => hasActiveChild(pathname, child)));
};

interface NavItemProps {
  item: NavLink;
  pathname: string;
  selectedItem?: string;
  depth?: number;
  expandedIds: Record<string, boolean>;
  onToggleExpand: (id: string) => void;
  onSelect: (url: string, shouldOpenNewTab?: boolean) => void;
}

function NavItem({
  item,
  pathname,
  selectedItem,
  depth = 0,
  expandedIds,
  onToggleExpand,
  onSelect,
}: NavItemProps) {
  const hasChildren = Boolean(item.isSubLinks && item.sublinks?.length);
  const isExpanded = expandedIds[item.id];
  const isActive = Boolean(
    item.url && selectedItem && normalizeNavUrl(item.url) === selectedItem
  );

  const handleClick = () => {
    if (hasChildren) {
      onToggleExpand(item.id);
      return;
    }
    if (item.url) {
      onSelect(item.url, item.shouldOpenNewTab);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.navItem} ${depth > 0 ? styles.navItemNested : ""} ${
          isActive ? styles.navItemActive : ""
        }`}
        onClick={handleClick}
      >
        {item.icon ? <span className={styles.navIcon}>{item.icon}</span> : null}
        <span className={styles.navLabel} title={item.label}>
          {item.label}
        </span>
        {hasChildren ? (
          isExpanded ? (
            <KeyboardArrowDownIcon className={styles.navChevron} />
          ) : (
            <KeyboardArrowRightIcon className={styles.navChevron} />
          )
        ) : null}
      </button>
      {hasChildren && isExpanded
        ? item.sublinks?.map((child) => (
            <NavItem
              key={`${child.id}-${child.url ?? child.label}`}
              item={child}
              pathname={pathname}
              selectedItem={selectedItem}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
            />
          ))
        : null}
    </>
  );
}

const WorkspaceSidebar = ({
  navItems,
  selectedItem,
  onSelect,
  workspaceName,
  workspaceIconSrc,
  workspaceAccentColor = "#3762e3",
  showProductSwitcher = false,
  groupIcon,
  isProductDropdownOpen = false,
  productItems = [],
  onProductSwitcherClick,
  onProductSelect,
}: WorkspaceSidebarProps) => {
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const { pathname } = useLocation();

  useEffect(() => {
    const nextExpanded: Record<string, boolean> = {};
    const markExpanded = (items: NavLink[]) => {
      items.forEach((item) => {
        if (item.isSubLinks && item.sublinks?.length) {
          if (hasActiveChild(pathname, item)) {
            nextExpanded[item.id] = true;
          }
          markExpanded(item.sublinks);
        }
      });
    };
    markExpanded(navItems);
    setExpandedIds((prev) => ({ ...prev, ...nextExpanded }));
  }, [pathname, navItems]);

  const onToggleExpand = (id: string) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNavSelect = (url: string, shouldOpenNewTab?: boolean) => {
    if (shouldOpenNewTab) {
      window.open(url, "_blank");
      return;
    }
    onSelect(normalizeNavUrl(url));
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        <div className={styles.workspaceHeader}>
          <div
            className={styles.workspaceIcon}
            style={{ backgroundColor: workspaceAccentColor }}
          >
            {workspaceIconSrc ? (
              <img src={workspaceIconSrc} alt={workspaceName} />
            ) : null}
          </div>
          <div className={styles.workspaceMeta}>
            <div className={styles.workspaceLabel}>WORKSPACE</div>
            <div className={styles.workspaceName}>{workspaceName}</div>
          </div>
          {showProductSwitcher ? (
            <button
              type="button"
              className={styles.switcherButton}
              onClick={onProductSwitcherClick}
              aria-label="Switch workspace product"
              aria-expanded={isProductDropdownOpen}
            >
              {groupIcon ?? <UnfoldMoreIcon className={styles.switcherIcon} />}
            </button>
          ) : null}
        </div>

        {isProductDropdownOpen && productItems.length > 0 ? (
          <div className={styles.productDropdown}>
            {productItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={styles.productItem}
                onClick={() => onProductSelect?.(item)}
              >
                {item.icon ? (
                  <div
                    className={styles.productIcon}
                    style={{ backgroundColor: item.accentColor ?? workspaceAccentColor }}
                  >
                    <img src={item.icon} alt={item.name} />
                  </div>
                ) : null}
                <span>{item.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <nav className={styles.nav}>
        {navItems.map((item) => (
          <div key={`${item.id}-${item.url ?? item.label}`} className={styles.navGroup}>
            <NavItem
              item={item}
              pathname={pathname}
              selectedItem={selectedItem}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onSelect={handleNavSelect}
            />
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default WorkspaceSidebar;
